import React, { useEffect, useState } from 'react';
import axios from 'axios';
import parse from 'html-react-parser';
import { Html } from 'react-pdf-html';
import {
   PDFDownloadLink,
   PDFViewer,
   Document,
   Page,
   Image,
   Text,
   View,
   StyleSheet,
   Link,
   Font,
} from '@react-pdf/renderer';


function CatalogShopGrid() {
   const [products, setProducts] = useState([]);
   const [settings, setSettings] = useState(window.GMWCP_PDF_DATA);
   const [isLoading, setIsLoading] = useState(true);
   const [isMobile, setIsMobile] = useState(false);
   const [currentPage, setCurrentPage] = useState(1);
   const [totalPages, setTotalPages] = useState(1);
   const [siteUrl, setSiteUrl] = useState(window.GMWCP_PDF_DATA['site_url']);
   const [pluginUrl, setPluginUrl] = useState(window.GMWCP_PDF_DATA['pluginurl']); 
   const [restapiUrl, setRestApiUrl] = useState(window.GMWCP_PDF_DATA['rest_api_url']); 
   const [rest_api_product, setRestApiProductUrl] = useState(window.GMWCP_PDF_DATA['rest_api_product']);  
   const [error, setError] = useState('');

   useEffect(() => {
      if (!siteUrl) return; // Exit early if siteUrl is empty

      Font.register({
         family: 'Roboto',
         fonts: [
            {
               src: `${pluginUrl}/build/fonts/Roboto-Regular.ttf`,
               fontWeight: 'normal',
            },
            {
               src: `${pluginUrl}/build/fonts/Roboto-Bold.ttf`,
               fontWeight: 'bold',
            },
            
         ],
      });
   }, [siteUrl]);

   useEffect(() => {
      if (!siteUrl) return; // Exit early if siteUrl is empty

      setIsMobile(/Mobi|Android/i.test(navigator.userAgent));

      const fetchProducts = async () => {
         try {
            let productsApiUrl = `${rest_api_product}`;
            let currentPage = 1;
            let allProducts = [];
            let totalPages = 1;

            while (currentPage <= totalPages) {
               const separator = productsApiUrl.includes('?') ? '&' : '?';
               let response;
               response = await axios.get(`${productsApiUrl}${separator}page=${currentPage}`);

               allProducts = [...allProducts, ...response.data];
               totalPages = response.headers['x-wp-totalpages'];
               setCurrentPage(currentPage);
               setTotalPages(totalPages);
               currentPage++;
            }

            setProducts(allProducts);
         } catch (error) {
            console.error('Error fetching products:', error);
            setError('Error fetching products');
         } finally {
            setIsLoading(false);
         }
      };
      fetchProducts();
   }, [siteUrl]);

   const groupByCategory = (products) => {
       const categoryMap = {};
       products.forEach((product) => {
          if (product.producat_cat && product.producat_cat.length > 0) {
             product.producat_cat.forEach((category) => {
                if (!categoryMap[category]) categoryMap[category] = [];
                categoryMap[category].push(product);
             });
          } else {
             if (!categoryMap['Uncategorized']) categoryMap['Uncategorized'] = [];
             categoryMap['Uncategorized'].push(product);
          }
       });
       return categoryMap;
    };
   // Chunk the array based on dynamic page break value
   function chunkArray(products, pageBreak, shopPerRowGrid) {
       const pageChunks = [];
       
       // Split products into chunks based on pageBreak
       for (let i = 0; i < products.length; i += pageBreak) {
           const pageProducts = products.slice(i, i + pageBreak);

           // Now break the page's products into rows based on shop_per_row_grid
           const rows = [];
           for (let j = 0; j < pageProducts.length; j += shopPerRowGrid) {
               const rowProducts = pageProducts.slice(j, j + shopPerRowGrid);
               rows.push(rowProducts);
           }
           
           // Add the rows (representing products on this page) to the pageChunks
           pageChunks.push(rows);
       }

       return pageChunks;
   }



   const ProductViewPdf = ({ products }) => {
      const styles = StyleSheet.create({
         body: {
            padding: 0,
            fontSize: 12,
            fontFamily: 'Roboto',
         },
         header: {
            fontSize: 12,
            marginBottom: 0,
            textAlign: 'center',
            paddingBottom: 10,
            backgroundColor: settings.setting?.gmpcp_hf_background_color || '#0084B4',
            color: settings.setting?.gmpcp_hf_text_color || '#f5f5f5',
            padding: 5,
         },
         galleryImagesContainer: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: 10,
            marginTop: 2,
            flexWrap: 'wrap',
            gap: 0,
         },
         galleryImage: {
            width: '33%',
         },
         footer: {
            
            textAlign: 'center',
            fontSize: 12,
            borderTop: '1px solid #ccc',
            padding: 5,
            backgroundColor: settings.setting?.gmpcp_hf_background_color || '#f5f5f5',
            color: settings.setting?.gmpcp_hf_text_color || '#f5f5f5',
         },
         contentSection: {
            padding: 10,
            gap: 10,
         },
         contentContainermaincolor:{
           backgroundColor: settings.setting?.gmpcp_background_color || '#f5f5f5',
            color: settings.setting?.gmpcp_text_color || '#f5f5f5',
          },
         contentContainermain:{
           
            fontSize:10,
            width:(100/settings.setting?.gmpcp_shop_per_row_grid)+"%"
         },
         rowContainer: {
            flexDirection: 'row',
            padding: 10,
            gap: 10,
         },
         
         productName: {
            fontSize: 16,
            fontWeight: 'bold',
            marginBottom: 10,
         },
         productLink: {
            marginTop: 10,
            color: 'blue',
            textDecoration: 'underline',
         },
         boldLabel: {
            fontWeight: 'bold',
         },
         contentpart: {
            marginTop: 5,
         },
         contentpartmain:{
           textAlign: settings.setting?.gmwcp_shop_text_align || 'left',
         },
         categoryTitle:{
            fontSize: 20,
            fontWeight: 'bold',
            paddingTop: 5,
            paddingBottom: 5,
            textAlign:'center',
         },
      });

      if (error) {
         return (
            <Document>
               <Page size="A4">
                  <View style={styles.body}>
                     <Text>Error: {error}</Text>
                  </View>
               </Page>
            </Document>
         );
      }

      if (!products || products.length === 0 || !settings) return null;

      const pageBreak = parseInt(settings.setting?.gmwcp_shop_pagebreak, 10) || 1;
      const shop_per_row_grid = parseInt(settings.setting?.gmpcp_shop_per_row_grid, 10) || 1;
      
      /*const productChunks = chunkArray(products, pageBreak, shop_per_row_grid);
      console.log(productChunks);*/
      const gmwcp_shop_show_category_wise = settings.setting?.gmwcp_shop_show_category_wise;
      if (gmwcp_shop_show_category_wise=='yes') {
        const categoryWiseProducts = groupByCategory(products);
        return (
          <Document>
            {Object.entries(categoryWiseProducts).map(([categoryName, categoryProducts], categoryIndex) => {
              const categoryChunks = chunkArray(categoryProducts, pageBreak, shop_per_row_grid);
              
              return categoryChunks.map((pageChunk, chunkIndex) => (
              <Page style={styles.body} wrap={false}>
                
                <View style={styles.header}>
                  <Text>{settings.setting?.gmpcp_header_text || ''}</Text>
                </View>
                
                <View style={styles.contentContainermaincolor} >
                    {chunkIndex === 0 && (
                       <View style={styles.categoryTitle}>
                          <Text>{categoryName}</Text>
                       </View>
                    )}
                    <View  style={[styles.contentpartmain]}>

                      
                      {/* Loop through each row within the page */}
                      {pageChunk.map((row, rowIndex) => (
                        <View key={rowIndex} style={styles.rowContainer}> {/* Row container for each row */}
                          {/* Loop through each product in the row */}
                          {row.map((product, productIndex) => (
                            <View key={productIndex} style={styles.contentContainermain}>
                              <View>
                                {settings.setting?.gmwcp_shop_show_hide?.includes('images') &&
                                  product.images && product.images.full && (
                                    <Image
                                      src={product.images.full}
                                      alt={product.name}
                                      style={styles.image}
                                    />
                                  )}
                                {settings.setting?.gmwcp_shop_show_hide?.includes('gallery') && (
                                  <View style={styles.galleryImagesContainer}>
                                    {product.gallery_images?.map((image, imgIndex) => (
                                      <Image
                                        key={imgIndex}
                                        src={image.src}
                                        alt={image.alt}
                                        style={styles.galleryImage}
                                      />
                                    ))}
                                  </View>
                                )}

                                {settings.setting?.gmwcp_shop_show_hide?.includes('title') && (
                                  <Text style={styles.productName}>{product.name}</Text>
                                )}
                                {settings.setting?.gmwcp_shop_show_hide?.includes('short_desc') && (
                                  <Text>{parse(product.short_description)}</Text>
                                )}
                                {settings.setting?.gmwcp_shop_show_hide?.includes('read_more') && (
                                  <Link src={product.permalink} style={styles.productLink}>
                                    {settings.setting?.gmpcp_trasnlation_read_more||''}{' '}
                                  </Link>
                                )}
                                {settings.setting?.gmwcp_shop_show_hide?.includes('sku') && (
                                  <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                      {settings.setting?.gmpcp_trasnlation_sku||''}{' '}
                                    </Text>
                                    {product.sku}
                                  </Text>
                                )}
                                {settings.setting?.gmwcp_shop_show_hide?.includes('price') && (
                                  <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                      {settings.setting?.gmpcp_trasnlation_price||''}{' '}
                                    </Text>
                                    {product.price}
                                  </Text>
                                )}
                                {settings.setting?.gmwcp_shop_show_hide?.includes('stock_status') && (
                                  <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                      {settings.setting?.gmpcp_trasnlation_stock||''}{' '}
                                    </Text>
                                    {product.stock_status}
                                  </Text>
                                )}
                                {settings.setting?.gmwcp_shop_show_hide?.includes('categories') && (
                                  <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                      {settings.setting?.gmpcp_trasnlation_categories||''}{' '}
                                    </Text>
                                    {product.producat_cat.join(', ')}
                                  </Text>
                                )}
                                {settings.setting?.gmwcp_shop_show_hide?.includes('tags') && (
                                  <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                      {settings.setting?.gmpcp_trasnlation_tags||''}{' '}
                                    </Text>
                                    {product.producat_tag.join(', ')}
                                  </Text>
                                )}

                                {product.gmwcp_custom_field && 
                                         Object.keys(product.gmwcp_custom_field).map((key, index) => {
                                           const gmwcp_custom_field = product.gmwcp_custom_field[key];
                                          console.log(key);
                                           if (settings.setting?.gmwcp_shop_show_hide?.includes(key)) {
                                             return (
                                               <Text key={index} style={styles.contentpart}>
                                                 <Text style={styles.boldLabel}>
                                                   {gmwcp_custom_field.label || ''}{' '}
                                                 </Text>
                                                 {gmwcp_custom_field.value || ''}{' '}
                                               </Text>
                                             );
                                           }
                                           return null; // Return null if the key is not included in `gmwcp_shop_show_hide`
                                         })
                                       }

                                       
                                {settings.setting?.gmwcp_shop_show_hide?.includes('attributes') && (
                                  <View style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>{settings.setting?.gmpcp_trasnlation_attribute||''}{' '}</Text>
                                    {product.attributes && typeof product.attributes === 'object' && !Array.isArray(product.attributes) ? (
                                      Object.entries(product.attributes).map(([key, value], index) => (
                                        <Text key={index}>
                                          <Text style={styles.boldLabel}>{key}</Text>: {value}
                                        </Text>
                                      ))
                                    ) : (
                                      product.attributes.map((field, index) => (
                                        <Text key={index}>
                                          <Text style={styles.boldLabel}>{field.key}</Text>: {field.value}
                                        </Text>
                                      ))
                                    )}
                                  </View>
                                )}
                              </View>

                              {settings.setting?.gmwcp_shop_show_hide?.includes('long_desc') && (
                                <View >
                                  <Html>
                                       {`
                                          <style>
                                          h1,h2,h3,h4,h5,h6{
                                             margin:0;
                                             font-size:12px;
                                             margin-bottom:2px;
                                          }
                                          </style>
                                          <div style="font-size:12px; ">
                                             ${product.description}
                                          </div>
                                       `}
                                    </Html>
                                </View>
                              )}
                            </View>
                          ))}
                        </View>
                      ))}
                    </View>
                </View>

                <View style={styles.footer}>
                  <Text>{settings.setting?.gmpcp_footer_text || ''}</Text>
                </View>
              </Page>
            ));
           })}
          </Document>
        );
      }else{
        const productChunks = chunkArray(products, pageBreak, shop_per_row_grid);
        return (
        <Document>
          {productChunks.map((page, chunkIndex) => (
            <Page key={chunkIndex}  style={styles.body} wrap={false}>
              
              <View style={styles.header}>
                <Text>{settings.setting?.gmpcp_header_text || ''}</Text>
              </View>
              <View style={styles.contentContainermaincolor} >
                <View  style={[styles.contentpartmain]}>
                  {/* Loop through each row within the page */}
                  {page.map((row, rowIndex) => (
                    <View key={rowIndex} style={styles.rowContainer}> {/* Row container for each row */}
                      {/* Loop through each product in the row */}
                      {row.map((product, productIndex) => (
                        <View key={productIndex} style={styles.contentContainermain}>
                          <View>
                            {settings.setting?.gmwcp_shop_show_hide?.includes('images') &&
                              product.images && product.images.full && (
                                <Image
                                  src={product.images.full}
                                  alt={product.name}
                                  style={styles.image}
                                />
                              )}
                            {settings.setting?.gmwcp_shop_show_hide?.includes('gallery') && (
                              <View style={styles.galleryImagesContainer}>
                                {product.gallery_images?.map((image, imgIndex) => (
                                  <Image
                                    key={imgIndex}
                                    src={image.src}
                                    alt={image.alt}
                                    style={styles.galleryImage}
                                  />
                                ))}
                              </View>
                            )}

                            {settings.setting?.gmwcp_shop_show_hide?.includes('title') && (
                              <Text style={styles.productName}>{product.name}</Text>
                            )}
                            {settings.setting?.gmwcp_shop_show_hide?.includes('short_desc') && (
                              <Text>{parse(product.short_description)}</Text>
                            )}
                            {settings.setting?.gmwcp_shop_show_hide?.includes('read_more') && (
                              <Link src={product.permalink} style={styles.productLink}>
                                {settings.setting?.gmpcp_trasnlation_read_more||''}{' '}
                              </Link>
                            )}
                            {settings.setting?.gmwcp_shop_show_hide?.includes('sku') && (
                              <Text style={styles.contentpart}>
                                <Text style={styles.boldLabel}>
                                  {settings.setting?.gmpcp_trasnlation_sku||''}{' '}
                                </Text>
                                {product.sku}
                              </Text>
                            )}
                            {settings.setting?.gmwcp_shop_show_hide?.includes('price') && (
                              <Text style={styles.contentpart}>
                                <Text style={styles.boldLabel}>
                                  {settings.setting?.gmpcp_trasnlation_price||''}{' '}
                                </Text>
                                {product.price}
                              </Text>
                            )}
                            {settings.setting?.gmwcp_shop_show_hide?.includes('stock_status') && (
                              <Text style={styles.contentpart}>
                                <Text style={styles.boldLabel}>
                                  {settings.setting?.gmpcp_trasnlation_stock||''}{' '}
                                </Text>
                                {product.stock_status}
                              </Text>
                            )}
                            {settings.setting?.gmwcp_shop_show_hide?.includes('categories') && (
                              <Text style={styles.contentpart}>
                                <Text style={styles.boldLabel}>
                                  {settings.setting?.gmpcp_trasnlation_categories||''}{' '}
                                </Text>
                                {product.producat_cat.join(', ')}
                              </Text>
                            )}
                            {settings.setting?.gmwcp_shop_show_hide?.includes('tags') && (
                              <Text style={styles.contentpart}>
                                <Text style={styles.boldLabel}>
                                  {settings.setting?.gmpcp_trasnlation_tags||''}{' '}
                                </Text>
                                {product.producat_tag.join(', ')}
                              </Text>
                            )}

                            {product.gmwcp_custom_field && 
                                     Object.keys(product.gmwcp_custom_field).map((key, index) => {
                                       const gmwcp_custom_field = product.gmwcp_custom_field[key];
                                      console.log(key);
                                       if (settings.setting?.gmwcp_shop_show_hide?.includes(key)) {
                                         return (
                                           <Text key={index} style={styles.contentpart}>
                                             <Text style={styles.boldLabel}>
                                               {gmwcp_custom_field.label || ''}{' '}
                                             </Text>
                                             {gmwcp_custom_field.value || ''}{' '}
                                           </Text>
                                         );
                                       }
                                       return null; // Return null if the key is not included in `gmwcp_shop_show_hide`
                                     })
                                   }

                                   
                            {settings.setting?.gmwcp_shop_show_hide?.includes('attributes') && (
                              <View style={styles.contentpart}>
                                <Text style={styles.boldLabel}>{settings.setting?.gmpcp_trasnlation_attribute||''}{' '}</Text>
                                {product.attributes && typeof product.attributes === 'object' && !Array.isArray(product.attributes) ? (
                                  Object.entries(product.attributes).map(([key, value], index) => (
                                    <Text key={index}>
                                      <Text style={styles.boldLabel}>{key}</Text>: {value}
                                    </Text>
                                  ))
                                ) : (
                                  product.attributes.map((field, index) => (
                                    <Text key={index}>
                                      <Text style={styles.boldLabel}>{field.key}</Text>: {field.value}
                                    </Text>
                                  ))
                                )}
                              </View>
                            )}
                          </View>

                          {settings.setting?.gmwcp_shop_show_hide?.includes('long_desc') && (
                            <View >
                              <Html>
                                   {`
                                      <style>
                                      h1,h2,h3,h4,h5,h6{
                                         margin:0;
                                         font-size:12px;
                                         margin-bottom:2px;
                                      }
                                      </style>
                                      <div style="font-size:12px; ">
                                         ${product.description}
                                      </div>
                                   `}
                                </Html>
                            </View>
                          )}
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
              <View style={styles.footer}>
                <Text>{settings.setting?.gmpcp_footer_text || ''}</Text>
              </View>
            </Page>
          ))}
        </Document>
      );
      }
   };

   return (
      <div className="gmwcpApp">
         {isLoading ? (
            <div><div class="loader"></div><div class="loading-text">Loading products...{currentPage} of {totalPages}</div></div>
         ) : (
         <>
            <PDFDownloadLink
                document={<ProductViewPdf products={products} />}
                fileName={`${settings.setting?.gmwcp_shop_download_file_name || 'Download'}.pdf`}
            >
                {({ loading }) => (
                    <button className="pdf-download-button">
                        {loading
                            ? 'Loading document...'
                            : settings.setting?.gmpcp_trasnlation_category || 'Download'}
                    </button>
                )}
            </PDFDownloadLink>

            <PDFViewer style={{ width: '100%', height: '100vh' }}>
               <ProductViewPdf products={products} />
            </PDFViewer>
            </>
         )}
      </div>
   );
}

export default CatalogShopGrid;
